import {
  GET_TRAINING_BOXES,
  REMOVE_BOX,
  tokenOptions,
} from "../../backend/graphql";
import { useAppSelector, useHistoryState } from "../../app/hooks";
import { isReadonly } from "../../app/features/auth/authTypes";
import { Box } from "@mui/material";
import { backendURL } from "../../constants";
import AutoSizer, { Size } from "react-virtualized-auto-sizer";
import { useMutation, useQuery } from "@apollo/client";
import ActionIconButton from "../widgets/ActionIconButton";
import BuildIcon from "@mui/icons-material/Build";
import PaginatedGallery from "../widgets/PaginatedGallery";
import ThumbnailCard from "../widgets/ThumbnailCard";
import SectionPaper from "../widgets/SectionPaper";
import PageTitle from "../widgets/PageTitle";
import DeleteIconButton from "../widgets/DeleteIconButton";
import { galleryLayout, GALLERY_CHROME } from "../widgets/galleryLayout";

type LearningBoxesGridProps = {
  computedWidth: number;
  computedHeight: number;
};

function LearningBoxesGrid({
  computedWidth,
  computedHeight,
}: LearningBoxesGridProps) {
  const token = useAppSelector((state) => state.auth.token);
  const readonly = isReadonly(useAppSelector((state) => state.auth.role));
  const [currentPage, setCurrentPage] = useHistoryState("currentPage", 1);
  const { cols, rows, perPage } = galleryLayout(
    computedWidth,
    150,
    175,
    0,
    computedHeight,
  );
  const [removeBox] = useMutation(REMOVE_BOX, tokenOptions(token));

  const { loading, error, data, refetch } = useQuery(GET_TRAINING_BOXES, {
    ...tokenOptions(token),
    variables: { take: perPage, skip: (currentPage - 1) * perPage, filter: "" },
  });

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error.message}</p>;

  const removeButton = (imageID: string) => {
    removeBox({ variables: { id: imageID } }).then(() => refetch());
  };

  const items = data.trainingBoxes.items;

  return (
    <PaginatedGallery
      width={computedWidth}
      height={computedHeight}
      cols={cols}
      rows={rows}
      page={currentPage}
      onPage={setCurrentPage}
      hasNext={items.length >= perPage}
    >
      {items.map((image: any) => (
        <ThumbnailCard
          key={image.id}
          size={128}
          src={`${backendURL}/training/box/${image.id}/mask`}
          hoverSrc={`${backendURL}/training/box/${image.id}/grayscale`}
          alt={`Mask ${image.id}`}
          // A read-only user looks at the samples; it neither edits nor removes them.
          actions={
            readonly ? undefined : (
              <>
                <ActionIconButton
                  onClick={() =>
                    window.open(`/learning/box/edit/${image.id}`, "_blank")
                  }
                >
                  <BuildIcon fontSize="small" />
                </ActionIconButton>
                <DeleteIconButton onClick={() => removeButton(image.id)} />
              </>
            )
          }
        />
      ))}
    </PaginatedGallery>
  );
}

const LearningBoxes = () => {
  return (
    <SectionPaper
      sx={{
        height: `calc(100vh - ${GALLERY_CHROME}px)`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <PageTitle />
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <AutoSizer>
          {(size: Size) => (
            <LearningBoxesGrid
              computedWidth={size.width}
              computedHeight={size.height}
            />
          )}
        </AutoSizer>
      </Box>
    </SectionPaper>
  );
};

export default LearningBoxes;
